import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Typography, Box, Card, CardContent} from 'views/components';
import {Translate} from 'views/module';
import {CloudUpload, CheckCircle} from 'views/icons';

import Backend from 'utils/backend';

import {useDropzone} from 'react-dropzone';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import mammoth from 'mammoth';

import FileLoadingSection from './fileLoading';

// Set worker source for PDF.js
if (pdfjsLib.GlobalWorkerOptions) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
}

// ResumeParser utility, can probably move to component
const parseResume = async (file) => {
  const extension = file.name.split('.').pop().toLowerCase();
  const arrayBuffer = await file.arrayBuffer();

  if (extension === 'pdf') {
    const pdf = await pdfjsLib.getDocument({data: arrayBuffer}).promise;
    let text = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      text += content.items.map((item) => item.str).join(' ') + '\n';
    }
    return text.trim();
  }

  if (extension === 'docx') {
    const result = await mammoth.extractRawText({arrayBuffer});
    return result.value;
  }

  return await file.text();
};

const ResumeSection = ({setValidity, setIsProcessing: setParentProcessing, caregiverResumeData, onResumeDataChange}) => {
  const [error, setError] = React.useState('');
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState(0);
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  // Check if resume already exists
  const hasExistingResume = caregiverResumeData?.caregiverRn;
  const existingResumeName = caregiverResumeData?.fileName || 'Your Resume';

  // Always set validity to true since resume upload is optional
  React.useEffect(() => {
    if (setValidity) {
      setValidity(true);
    }
  }, [setValidity]);

  // Notify parent of processing state
  React.useEffect(() => {
    if (setParentProcessing) {
      setParentProcessing(isProcessing);
    }
  }, [isProcessing, setParentProcessing]);

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setError('');
    setIsProcessing(true);
    setCurrentStep(0);

    try {
      // Step 0: Extracting text
      setCurrentStep(0);
      const text = await parseResume(file);
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Step 1: Understanding experience
      setCurrentStep(1);
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Step 2: Detecting skills
      setCurrentStep(2);
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Call backend with extracted text
      const result = await Backend.caregiver.createCaregiverFromResume(text);

      // Step 3: Preparing your application
      setCurrentStep(3);
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Complete processing
      setCurrentStep(4);

      // Save to localStorage immediately if successfully processed
      if (result.successfullyProcessed) {
        const resumeData = {
          ...result,
          fileName: file.name,
        };

        // Save to localStorage
        localStorage.setItem('CaregiverInterview-ResumeData', JSON.stringify(resumeData));

        // Notify parent component
        if (onResumeDataChange) {
          onResumeDataChange(resumeData);
        }
      }

      setIsSubmitted(true);
      await new Promise((resolve) => setTimeout(resolve, 500));
    } catch (err) {
      console.error('Failed to process resume:', err);
      setError(`Failed to process ${file.name}: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // useDropzone must be called before any early returns
  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
  });

  // Show loading screen while processing
  if (isProcessing) {
    return <FileLoadingSection currentStep={currentStep} />;
  }

  // Show read-only view if resume already exists
  if (hasExistingResume && !isSubmitted) {
    return (
      <Box sx={{py: 3, px: {lg: 18, md: 5, xs: 1}}}>
        <Stack spacing={4} alignItems="center">
          <Box textAlign="center">
            <Typography variant="h3" component="h1" gutterBottom>
              <Translate expression="caregiverInterview.Resume.Title" />
            </Typography>
          </Box>

          <Card sx={{width: '100%', maxWidth: 800, mt: 4}}>
            <CardContent sx={{p: 4, textAlign: 'center'}}>
              <CheckCircle sx={{fontSize: 64, color: 'success.main', mb: 2}} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                <Translate expression="caregiverInterview.Resume.AlreadySubmitted.Title" />
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{mb: 2}}>
                <Translate expression="caregiverInterview.Resume.AlreadySubmitted.Message" />
              </Typography>
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  display: 'inline-block',
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {existingResumeName}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    );
  }

  // Show success message after submission
  if (isSubmitted) {
    return (
      <Box sx={{py: 3, px: {lg: 18, md: 5, xs: 1}}}>
        <Stack spacing={4} alignItems="center">
          <Box textAlign="center">
            <Typography variant="h3" component="h1" gutterBottom>
              <Translate expression="caregiverInterview.Resume.Title" />
            </Typography>
          </Box>

          <Card sx={{width: '100%', maxWidth: 800, mt: 4}}>
            <CardContent sx={{p: 4, textAlign: 'center'}}>
              <CheckCircle sx={{fontSize: 64, color: 'success.main', mb: 2}} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                <Translate expression="caregiverInterview.Resume.Success.Title" />
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{mb: 3}}>
                <Translate expression="caregiverInterview.Resume.Success.Message" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Translate expression="caregiverInterview.Resume.Success.NextStep" />
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{py: 3, px: {lg: 18, md: 5, xs: 1}}}>
      <Stack spacing={4} alignItems="center">
        {/* Header Section */}
        <Box textAlign="center">
          <Typography variant="h3" component="h1" gutterBottom>
            <Translate expression="caregiverInterview.Resume.Title" />
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{maxWidth: 600}}>
            <Translate expression="caregiverInterview.Resume.Subtitle" />
          </Typography>
        </Box>

        {/* Benefits Cards */}
        <Stack
          direction={{xs: 'column', md: 'row'}}
          spacing={3}
          sx={{width: '100%', mt: 4}}
        >
          <Card sx={{flex: 1}}>
            <CardContent sx={{textAlign: 'center', p: 3}}>
              <CloudUpload sx={{fontSize: 40, color: 'primary.main', mb: 2}} />
              <Typography variant="h6" gutterBottom>
                <Translate expression="caregiverInterview.Resume.EasyUpload" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Translate expression="caregiverInterview.Resume.EasyUploadDesc" />
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{flex: 1}}>
            <CardContent sx={{textAlign: 'center', p: 3}}>
              <CheckCircle sx={{fontSize: 40, color: 'primary.main', mb: 2}} />
              <Typography variant="h6" gutterBottom>
                <Translate expression="caregiverInterview.Resume.SecureProcess" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Translate expression="caregiverInterview.Resume.SecureProcessDesc" />
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        {/* Upload Area */}
        <Card sx={{width: '100%', maxWidth: 800, mt: 4}}>
          <CardContent sx={{p: 4}}>
            <Box
              {...getRootProps()}
              sx={{
                'border': '2px dashed',
                'borderColor': isDragActive ? 'primary.main' : 'divider',
                'borderRadius': 2,
                'p': 6,
                'textAlign': 'center',
                'cursor': 'pointer',
                'transition': 'all 0.2s',
                'bgcolor': isDragActive ? 'action.hover' : 'transparent',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              <input {...getInputProps()} />

              <CloudUpload sx={{fontSize: 48, color: 'text.secondary', mb: 2}} />

              <Box>
                <Typography variant="h6" gutterBottom>
                  {
                    isDragActive &&
                    'Drop your files here...'
                  }
                  {
                    !isDragActive &&
                    <Translate expression="caregiverInterview.Resume.DropZoneTitle" />
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Translate expression="caregiverInterview.Resume.DropZoneDesc" />
                </Typography>
              </Box>
            </Box>

            {/* Error Message */}
            {
              error &&
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: 'error.lighter',
                  border: '1px solid',
                  borderColor: 'error.light',
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" color="error.main">
                  <Translate expression="caregiverInterview.Resume.Error" />
                </Typography>
                <Typography variant="body2" color="error.main" sx={{mt: 0.5}}>
                  {error}
                </Typography>
              </Box>
            }
          </CardContent>
        </Card>

        {/* Additional Info */}
        <Box sx={{textAlign: 'center', mt: 4}}>
          <Typography variant="body2" color="text.secondary">
            <Translate expression="caregiverInterview.Resume.PrivacyNote" />
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

ResumeSection.propTypes = {
  setValidity: PropTypes.func,
  setIsProcessing: PropTypes.func,
  caregiverResumeData: PropTypes.object,
  onResumeDataChange: PropTypes.func,
};

export default ResumeSection;
